import { zodResolver } from '@hookform/resolvers/zod';
import { Radio } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useCreateSource } from '../hooks/useCreateSource';
import { createSourceSchema, type CreateSourceFormValues } from '../schemas/sources-schema';
import { STRING_TO_SOURCE_TYPE_MAP, type SourceTypeString } from '../types/sources.types';
import {
  CommunicatesFields,
  // GoogleTrendsFields, // TODO: not implemented yet
  OpenMediaFields,
  SocialMediaFields,
  SubscriptionMediaFields,
} from './SourceTypeFields';
import { getErrorFromAxios } from '@/core/api/error-messages';
import { CountryHierarchyMultiSelect } from '@/features/countries/components/CountryHierarchyMultiSelect';
import { RegionHierarchyMultiSelect } from '@/features/regions/components/RegionHierarchyMultiSelect';
import { RegionSelector, RegionSelectorLabels } from '@/features/regions/types/regions.types';
import { ConfirmDialog } from '@/shared/components/ahkinia/ConfirmDialog/ConfirmDialog';
import { Notification } from '@/shared/components/ahkinia/Notification/notification';
import { SelectInput, SelectItem } from '@/shared/components/ahkinia/Select/select';
import { Switch } from '@/shared/components/ahkinia/Switch/switch';
import { FormControl, FormField, FormItem, FormMessage } from '@/shared/components/ui/form';
import { Input } from '@/shared/components/ui/input';
import { DialogFooter as ModalFooter, ModalForm } from '@/shared/components/ui/modal-form';
import { UnifiedButton } from '@/shared/components/ui/unified-button';

interface CreateSourceModalProps {
  open: boolean;
  onClose: () => void;
}

const SOURCE_TYPE_OPTIONS = [
  { label: 'Medios Abiertos', value: 'MediosAbiertos' },
  { label: 'Medios Suscripción', value: 'MediosSuscripcion' },
  { label: 'Redes Sociales', value: 'RedesSociales' },
  // { label: 'Tendencias - Google Trends', value: 'GoogleTrends' }, // TODO: not implemented yet
  { label: 'Comunicados', value: 'Comunicados' },
];

/**
 * Modal component for creating a new information source
 * Features:
 * - Dynamic fields based on source type
 * - Form validation with Zod
 * - Confirmation dialog before submission
 * - Error handling with user-friendly messages
 *
 * Implements the pattern from CreateAdminUserModal with adaptations for dynamic fields
 */
export function CreateSourceModal({ open, onClose }: CreateSourceModalProps) {
  const { mutate, isPending } = useCreateSource();
  const [pendingValues, setPendingValues] = useState<CreateSourceFormValues | null>(null);

  const form = useForm<CreateSourceFormValues>({
    resolver: zodResolver(createSourceSchema),
    defaultValues: {
      name: '',
      type: undefined,
      updateIntervalHours: 24,
      countryIds: undefined,
      selector: undefined,
      regionIds: [],
      isEnabled: true,
      baseUrl: '',
      username: '',
      password: '',
      keywords: [],
    },
    mode: 'onChange',
  });

  const sourceType = form.watch('type');
  const selectedCountryIds = form.watch('countryIds') ?? [];
  const selectedSelector = form.watch('selector');
  const countryIdsKey = selectedCountryIds.join(',');

  useEffect(() => {
    form.setValue('regionIds', []);
  }, [countryIdsKey, selectedSelector, form]);

  const onSubmit = (values: CreateSourceFormValues) => {
    setPendingValues(values);
  };

  const handleConfirm = () => {
    if (!pendingValues || !pendingValues.type) return;

    const payload = {
      ...pendingValues,
      type: STRING_TO_SOURCE_TYPE_MAP[pendingValues.type],
    };

    mutate(payload, {
      onSuccess: () => {
        setPendingValues(null);
        form.reset();
        onClose();
      },
      onError: (error) => {
        const errorInfo = getErrorFromAxios(error);
        const errorCode = (error as { response?: { data?: { title?: string } } })?.response?.data
          ?.title;

        if (errorCode === 'Source.NameAlreadyExists') {
          form.setError('name', { message: errorInfo.message });
        } else if (errorCode === 'Source.UrlAlreadyExists' || errorCode === 'Source.Url.Invalid') {
          form.setError('baseUrl', { message: errorInfo.message });
        } else if (errorCode === 'Source.UpdateInterval.Invalid') {
          form.setError('updateIntervalHours', { message: errorInfo.message });
        } else if (
          errorCode === 'Source.RegionIds.Empty' ||
          errorCode === 'Source.RegionIds.Invalid'
        ) {
          form.setError('regionIds', { message: errorInfo.message });
        } else if (errorCode === 'Source.BaseUrl.Required') {
          form.setError('baseUrl', { message: errorInfo.message });
        } else if (errorCode === 'Source.Username.Required') {
          form.setError('username', { message: errorInfo.message });
        } else if (errorCode === 'Source.Password.Required') {
          form.setError('password', { message: errorInfo.message });
        } else if (errorCode === 'Source.Network.Required') {
          form.setError('network', { message: errorInfo.message });
        } else if (errorCode === 'Source.Keywords.Empty') {
          form.setError('keywords', { message: errorInfo.message });
        } else {
          Notification.error({ title: errorInfo.title, description: errorInfo.message });
        }
      },
    });
  };

  const handleClose = () => {
    if (!isPending) {
      form.reset();
      setPendingValues(null);
      onClose();
    }
  };

  const handleTypeChange = (value: string) => {
    form.setValue('type', value as SourceTypeString, { shouldValidate: true });
    // Reset type-specific fields when type changes
    form.setValue('baseUrl', '');
    form.setValue('username', '');
    form.setValue('password', '');
    form.resetField('network');
    form.setValue('keywords', []);
  };

  const getSelectedTypeLabel = () => {
    return SOURCE_TYPE_OPTIONS.find((o) => o.value === sourceType)?.label || '';
  };

  return (
    <>
      <ModalForm
        open={open}
        headerIcon={<Radio />}
        onOpenChange={handleClose}
        title="Registrar fuente de información"
        description="Configura una nueva fuente para la ingesta de datos en el sistema."
        className="sm:max-w-[600px]"
        form={form}
        onSubmit={onSubmit}
        footer={
          <ModalFooter>
            <div className="flex flex-row justify-end w-full gap-4">
              <UnifiedButton
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isPending}
                data-testid="create-source-cancel-btn"
              >
                Cancelar
              </UnifiedButton>
              <UnifiedButton
                type="submit"
                variant="outline"
                actionType="submit"
                disabled={isPending}
                data-testid="create-source-submit-btn"
              >
                Guardar
              </UnifiedButton>
            </div>
          </ModalFooter>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-[1.23rem]">
          {/* Nombre de la fuente */}
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <Input
                    label="Nombre de la fuente"
                    placeholder="Ingresar nombre"
                    autoComplete="off"
                    {...field}
                    disabled={isPending}
                    error={form.formState.errors.name?.message}
                    data-testid="create-source-name-input"
                  />
                </FormControl>
              </FormItem>
            )}
          />

          {/* Tipo de fuente */}
          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <SelectInput
                    label="Tipo de fuente"
                    value={field.value ?? ''}
                    onValueChange={handleTypeChange}
                    disabled={isPending}
                    placeholder="Seleccionar tipo"
                    error={form.formState.errors.type?.message}
                    data-testid="create-source-type-select"
                  >
                    {SOURCE_TYPE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectInput>
                </FormControl>
              </FormItem>
            )}
          />

          {/* Intervalo de actualización */}
          <FormField
            control={form.control}
            name="updateIntervalHours"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <Input
                    label="Intervalo de actualización (horas)"
                    placeholder="24"
                    type="number"
                    min={1}
                    {...field}
                    onChange={(e) => field.onChange(Number(e.target.value) || 0)}
                    disabled={isPending}
                    error={form.formState.errors.updateIntervalHours?.message}
                    data-testid="create-source-interval-input"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        {/* País, tipo de región y selector de regiones */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-[1.23rem] mt-[1.23rem]">
          <FormField
            control={form.control}
            name="countryIds"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <CountryHierarchyMultiSelect
                    label="País / Territorio"
                    placeholder="Seleccionar país"
                    value={field.value ?? []}
                    onValueChange={field.onChange}
                    disabled={isPending}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="selector"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <SelectInput
                    label="Tipo de región"
                    value={field.value?.toString() ?? ''}
                    onValueChange={(value) => {
                      // Radix Select fires onValueChange("") from its hidden native <select>
                      // when the controlled value changes before the native options are mounted.
                      // Number("") === 0, which would corrupt the field to PoliticaElectoral.
                      if (value === '') return;
                      field.onChange(Number(value));
                    }}
                    disabled={isPending}
                    placeholder="Seleccionar tipo"
                    error={form.formState.errors.selector?.message}
                  >
                    {Object.entries(RegionSelector).map(([key, value]) => (
                      <SelectItem key={key} value={value.toString()}>
                        {RegionSelectorLabels[value as RegionSelector]}
                      </SelectItem>
                    ))}
                  </SelectInput>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {selectedCountryIds.length > 0 && selectedSelector !== undefined && (
          <div className="mt-[1.23rem]">
            <FormField
              control={form.control}
              name="regionIds"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <RegionHierarchyMultiSelect
                      key={`${countryIdsKey}-${selectedSelector}`}
                      countryIds={selectedCountryIds}
                      selector={selectedSelector as RegionSelector}
                      value={field.value}
                      onValueChange={field.onChange}
                      label="Regiones asociadas"
                      placeholder="Seleccionar regiones"
                      disabled={isPending}
                      error={form.formState.errors.regionIds?.message}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        )}

        {/* Estado inicial (Habilitado por defecto) */}
        <div className="flex items-center gap-3 mt-6 pt-4 border-t border-outline">
          <FormField
            control={form.control}
            name="isEnabled"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={isPending}
                    data-testid="create-source-enabled-switch"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <label className="text-sm text-primary font-medium">Habilitar fuente por defecto</label>
        </div>

        {/* Campos dinámicos según tipo */}
        {sourceType === 'MediosAbiertos' && (
          <div className="mt-6 pt-6 border-t border-outline">
            <OpenMediaFields />
          </div>
        )}
        {sourceType === 'MediosSuscripcion' && (
          <div className="mt-6 pt-6 border-t border-outline">
            <SubscriptionMediaFields />
          </div>
        )}
        {sourceType === 'RedesSociales' && (
          <div className="mt-6 pt-6 border-t border-outline">
            <SocialMediaFields />
          </div>
        )}
        {/* TODO: not implemented yet
        {sourceType === 'GoogleTrends' && (
          <div className="mt-6 pt-6 border-t border-outline">
            <GoogleTrendsFields />
          </div>
        )}
        */}
        {sourceType === 'Comunicados' && (
          <div className="mt-6 pt-6 border-t border-outline">
            <CommunicatesFields />
          </div>
        )}
      </ModalForm>

      {/* Confirmation dialog */}
      <ConfirmDialog
        open={!!pendingValues}
        onOpenChange={(v) => !isPending && !v && setPendingValues(null)}
        title="Confirmar registro de fuente"
        description={
          <div className="flex flex-col gap-2">
            <p>
              Se registrará la fuente{' '}
              <span className="font-medium text-light">{pendingValues?.name}</span> de tipo{' '}
              <span className="font-medium text-light">{getSelectedTypeLabel()}</span>.
            </p>
            <p>
              Intervalo de actualización:{' '}
              <span className="font-medium text-light">
                {pendingValues?.updateIntervalHours} horas
              </span>
            </p>
            <p>
              Estado inicial:{' '}
              <span className="font-medium text-light">
                {pendingValues?.isEnabled ? 'Habilitada' : 'Deshabilitada'}
              </span>
            </p>
          </div>
        }
        onConfirm={handleConfirm}
        confirmLabel="Confirmar"
        confirmLoadingLabel="Guardando..."
        isPending={isPending}
        onCancel={() => setPendingValues(null)}
      />
    </>
  );
}
